import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Sparkles, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Dashboard card that generates a 2-3 sentence compliance briefing
 * from live exception + operational signals. Admin / PM / Compliance only.
 */
export default function ComplianceSummaryCard() {
  const [summary, setSummary] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/assistant/compliance-summary");
      setSummary(data.summary);
      setGeneratedAt(data.generated_at);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const timeLabel = generatedAt
    ? new Date(generatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div
      className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-5 mb-6 border border-gray-800 relative overflow-hidden"
      data-testid="compliance-summary-card"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-orange-500 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-mono flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" strokeWidth={2.5} />
              Compliance Briefing
            </div>
            <div className="font-display font-bold text-lg leading-none tracking-tight mt-1">
              Today's watch list
            </div>
          </div>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          data-testid="compliance-summary-refresh"
          className="flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-gray-700 text-gray-300 hover:text-white hover:border-white transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Analyzing" : "Refresh"}
        </button>
      </div>

      <div className="text-sm leading-relaxed text-gray-100 min-h-[3rem]" data-testid="compliance-summary-text">
        {loading && !summary && (
          <span className="text-gray-400 font-mono text-xs">Reading exceptions, queue, hauler docs…</span>
        )}
        {!loading && error && (
          <span className="text-red-300">⚠️ {error}</span>
        )}
        {summary && <span>{summary}</span>}
      </div>

      {timeLabel && (
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-mono mt-3">
          Generated {timeLabel}
        </div>
      )}
    </div>
  );
}
